import { useState } from "react";
import { BackToTop } from "./components/BackToTop";
import { FdsOperations } from "./components/FdsOperations";
import { LiveProofStrip } from "./components/LiveProofStrip";
import { Intro } from "./components/Intro";
import { Limitations } from "./components/Limitations";
import { Overview } from "./components/Overview";
import { Story } from "./components/Story";

/**
 * Five tabs: intro (what is FDS + what the author is saying), playground, structure
 * ("어떻게 만들었나"), the five-act making-of ("만든 이야기", split out per user
 * direction 2026-08-15), limitations. The intro is the default - a first-time visitor
 * should meet the argument before the buttons - and its bottom CTA hands over to the
 * playground tab.
 */
type TabId = "intro" | "operations" | "overview" | "story" | "limitations";

const TABS: { id: TabId; label: string }[] = [
  { id: "intro", label: "소개" },
  { id: "operations", label: "직접 해보기" },
  { id: "overview", label: "어떻게 만들었나" },
  { id: "story", label: "만든 이야기" },
  { id: "limitations", label: "한계와 다음 단계" },
];

export function App() {
  const [activeTab, setActiveTab] = useState<TabId>("intro");

  function openOperations() {
    setActiveTab("operations");
    window.scrollTo({ top: 0 });
  }

  function openVerifyFlow() {
    setActiveTab("operations");
    // The transfer section exists only after the tab renders; jump on the next frame.
    // scrollIntoView is missing in some environments (jsdom), hence the guard.
    requestAnimationFrame(() => {
      const target = document.getElementById("try-transfer");
      target?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    });
  }

  return (
    <>
      <LiveProofStrip onVerify={openVerifyFlow} />

      <header className="app-header">
        <h1>🛡️ SafeFDS</h1>
        <p>
          은행에 들어오는 송금을 AI가 순식간에 검사해서 위험한 것만 사람에게 넘기고 —{" "}
          <strong>이상해지면 스스로 멈추는</strong> 시스템. 직접 송금도 해보고, 공격해서
          멈추게도 해보세요.
        </p>
      </header>

      <nav className="tabs" role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            onClick={() => {
              setActiveTab(tab.id);
              // A new tab is a new page - starting mid-scroll from the previous tab's
              // position reads as broken, so every tab opens from its top.
              window.scrollTo({ top: 0 });
            }}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <main>
        {activeTab === "intro" && (
          <Intro onTryDemo={openOperations} onVerify={openVerifyFlow} />
        )}
        {activeTab === "operations" && <FdsOperations />}
        {activeTab === "overview" && <Overview onVerify={openVerifyFlow} />}
        {activeTab === "story" && <Story />}
        {activeTab === "limitations" && <Limitations />}
      </main>

      <BackToTop />

      <footer className="app-footer">
        <p>
          SafeFDS는 포트폴리오 프로젝트입니다. 실제 은행 시스템이 아니며, 모든 거래와 성과
          수치는 합성 데이터 기준입니다.
        </p>
      </footer>
    </>
  );
}
